import { latestRoundSignatures, type ApprovalLineDefinitionRow } from '@safetyhub/shared';
import type { ApprovalSignature, PendingApprovalItem } from '../../lib/reports';
import { isApprovalSignatureParked } from '../../lib/approvalSignatureParked';

/**
 * 결재함 카드의 진행 스텝퍼 노드 산출 — 렌더와 분리한 순수 로직.
 *
 * 정본 관계:
 * - `report.approvalLineDefinition`(레이어1)이 **단계 정의의 정본**이다. 아직 서명행이
 *   만들어지지 않은 단계까지 들고 있으므로 "앞으로 남은 결재"를 그리려면 이게 베이스여야 한다.
 * - `approvalSignatures`(레이어2)는 각 단계의 **실제 진행 결과**다. 정의 row에 level로 겹쳐 읽는다.
 * - 작성자 단계(`sourceType==='author'`)는 정책상 ApprovalSignature 행을 만들지 않는다.
 *   작성자 서명은 `report.authorSignedAt` / `authorSignerName` 컬럼에 있다.
 *
 * 두 레이어는 legacy/전환 데이터에서 실제로 어긋난다(정의에 없는 level의 서명행, 정의는
 * 있는데 서명행이 없는 단계, author 단계인데 서명행도 있는 건). 어긋나도 **결재자가 화면에서
 * 사라지지 않는 것**을 최우선으로 둔다 — 목록은 "누가 언제 처리했나"를 보는 화면이라
 * 정의에 맞춰 버리는 쪽이 정의에 없는 걸 덧붙이는 쪽보다 훨씬 위험하다.
 */

/**
 * `parked` = **담당자가 정해지지 않아 멈춘 칸**(결정
 * `2026-08-27-unresolved-approval-slot-park-and-resume.md`, S39).
 *
 * `current`와 갈라 두는 이유: 둘 다 "아직 처리 안 됨"이지만 **다음에 일어날 일이 다르다.**
 * `current`는 지정된 사람이 열기를 기다리는 중이고, `parked`는 **기다려도 아무 일도 일어나지
 * 않는다** — 누군가 담당자를 배정하거나 지정해야 비로소 움직인다. 같은 "대기 중"으로 그리면
 * 목록이 *"곧 누가 결재할 것"* 처럼 읽혀, 멈춰 있다는 사실이 아무에게도 보이지 않는다.
 */
export type ApprovalFlowNodeStatus = 'done' | 'current' | 'rejected' | 'wait' | 'parked';

export interface ApprovalFlowNode {
  /** React key. 정의 기반은 level, 고아 서명행은 서명 id로 만든다. */
  key: string;
  /** 단계 라벨 (정의 label ?? 서명행 stepLabel ?? `N단계`). */
  label: string;
  /** 표시할 사람 이름. */
  name: string;
  /** 처리 시각 ISO. 미처리면 null. */
  at: string | null;
  status: ApprovalFlowNodeStatus;
  /** 작성자(제출) 노드 여부 — 결재 단계가 아니라 시각 표기·현재단계 판정에서 제외된다. */
  isAuthor: boolean;
  /**
   * 정의에 없는 서명행에서 만들어진 노드 = **결재선 데이터 불일치 신호**.
   *
   * 결재함에 뜨는 문서(전부 COMPOSED)는 템플릿 `approvalLineMeta`(+현장 override)의 결재 정책이
   * 켜져 있어 제출 시 정의가 반드시 생성된다. 정책이 꺼진 양식은 정의도 결재 서명행도 만들지
   * 않으므로 애초에 결재함에 오지 않는다. 따라서 "정의에 없는 서명행"은 정상 케이스가
   * 아니라 **데이터가 깨졌다는 뜻**이다.
   *
   * 노드를 버리지는 않는다 — 그 서명행이 결재함에 뜬 이유이므로 지우면 카드에 진행 정보가
   * 사라진다. 대신 조용히 메우지 말고 드러낸다. 조용히 메웠던 탓에 COMPOSED 결재라인
   * 소실(2026-07-24 발생 → 07-28 발견)이 두 달간 가려졌고, 그동안 결재 완료본의 결재자
   * 서명이 화면에서 빠져 있었다.
   */
  isOrphan: boolean;
}

/** 정의 row에 author 단계 표시가 없는 구 데이터를 위한 라벨. */
const AUTHOR_STEP_LABEL = '제출';

/**
 * 이 단계가 작성자 칸인지 판별.
 * `sourceType`이 정본이고, 이 필드가 없던 구 정의는 `assignmentChain[0].type`으로 fallback한다
 * (rc `approvalLinesFromDefinition`·서버 getReportApprovalLines와 동일 규칙).
 */
function isAuthorRow(row: ApprovalLineDefinitionRow): boolean {
  return (row.sourceType ?? row.assignmentChain?.[0]?.type) === 'author';
}

function approverStatus(
  sig: ApprovalSignature | undefined,
  currentPendingSignature: ApprovalSignature | null,
): ApprovalFlowNodeStatus {
  // 정의엔 있는데 서명행이 아직 없는 단계 — 미도달.
  if (!sig) return 'wait';
  if (sig.status === 'REJECTED') return 'rejected';
  if (sig.status === 'APPROVED') return 'done';
  // ⚠️ **차례가 왔는지보다 먼저 본다.** 담당자가 없는 칸은 차례가 와도 아무 일이 일어나지
  //    않으므로 `current`("곧 결재됨")로 그리면 목록이 거짓말을 한다(S39).
  //    뒤쪽 칸이 대기여도 `parked`로 둔다 — 앞 칸이 끝나는 순간 문서가 거기서 멈추고,
  //    그 사실은 미리 보이는 편이 낫다(배정은 지금도 할 수 있다).
  if (isApprovalSignatureParked(sig)) return 'parked';
  if (currentPendingSignature && sig.id === currentPendingSignature.id) return 'current';
  return 'wait';
}

/**
 * 진행 스텝퍼 노드를 만든다.
 *
 * @param item 결재 대기 목록 항목
 * @param authorDisplayName 호출부가 getEvidenceDisplayName으로 계산한 작성자 표시명(탈퇴 처리 포함)
 */
export function buildApprovalFlowNodes(
  item: PendingApprovalItem,
  authorDisplayName: string,
): ApprovalFlowNode[] {
  const { report, signatures, currentPendingSignature } = item;

  // 반려 후 재제출은 round+1로 새 행을 만들고 이전 round 행은 DB에만 남긴다(화면에서는 숨김).
  // dev 데이터가 전부 round=1이어도 최신 round만 그리도록 방어한다(shared 정본 `latestRoundSignatures`).
  const latest = latestRoundSignatures(signatures);

  const definition = Array.isArray(report.approvalLineDefinition)
    ? [...report.approvalLineDefinition].sort((a, b) => a.level - b.level)
    : [];

  const consumed = new Set<string>();
  const nodes: ApprovalFlowNode[] = [];
  let hasAuthorRow = false;

  // key는 level이 아니라 정의 배열의 위치로 만든다 — legacy 정의에 같은 level이 중복으로
  // 들어 있어도 React key가 충돌하지 않아야 한다.
  for (let index = 0; index < definition.length; index++) {
    const row = definition[index];
    // 같은 level에 서명행이 여럿이어도 하나만 소비한다(나머지는 고아로 뒤에 붙어 보존된다).
    const matched = latest.find((sig) => sig.approvalLevel === row.level && !consumed.has(sig.id));
    if (matched) consumed.add(matched.id);

    if (isAuthorRow(row)) {
      hasAuthorRow = true;
      // author 단계는 서명행을 만들지 않는 게 정책이지만, 전환기 데이터엔 같은 level에
      // 서명행이 남아 있는 건이 있다. 그 행은 여기서 흡수해 중복 노드가 생기지 않게 한다.
      const signedAt = report.authorSignedAt ?? matched?.signedAt ?? null;
      nodes.push({
        key: `def-${index}`,
        label: row.label || AUTHOR_STEP_LABEL,
        name: report.authorSignerName || row.memberName || authorDisplayName || '-',
        at: signedAt,
        status: signedAt ? 'done' : 'wait',
        isAuthor: true,
        isOrphan: false,
      });
      continue;
    }

    const status = approverStatus(matched, currentPendingSignature);
    nodes.push({
      key: `def-${index}`,
      label: row.label || matched?.stepLabel || `${row.level}단계`,
      // ⚠️ 대기 칸의 `signerName`은 서버 폴백 `(담당자)`라 **사람 이름처럼 보인다** — 그대로
      //    쓰면 "담당자 지정 필요"라고 말하면서 옆에 담당자 이름이 있는 모순이 된다(I-1과 같은 축).
      //    아직 아무도 없는 것이 사실이므로 이름 자리는 비운다.
      name: status === 'parked' ? '-' : matched?.signerName || row.memberName || '-',
      at: matched?.signedAt ?? null,
      status,
      isAuthor: false,
      isOrphan: false,
    });
  }

  // 정의에 없는 level의 서명행 — 버리지 않고 뒤에 덧붙이되 **불일치로 표시**한다.
  // 지우면 그 서명행이 결재함에 뜬 이유가 화면에서 사라지고, 조용히 붙이면 데이터가 깨진
  // 사실이 가려진다(ApprovalFlowNode.isOrphan 주석 참조).
  // 정의가 애초에 비어 있으면 대조 기준이 없으므로 불일치로 보지 않는다.
  const orphans = latest
    .filter((sig) => !consumed.has(sig.id))
    .sort((a, b) => a.approvalLevel - b.approvalLevel);

  for (const sig of orphans) {
    nodes.push({
      key: `sig-${sig.id}`,
      label: sig.stepLabel || `${sig.approvalLevel}단계`,
      name: sig.signerName || '-',
      at: sig.signedAt ?? null,
      status: approverStatus(sig, currentPendingSignature),
      isAuthor: false,
      isOrphan: definition.length > 0,
    });
  }

  // 정의에 author 단계가 없는 경우(정의 자체가 null이거나, L1이 delegatedTo 등으로 잡힌 건)
  // 에도 작성자 서명이 있으면 제출 노드를 앞에 세운다 — 목록은 진행 타임라인을 보는 화면이고,
  // '제출'은 결재 단계가 아니라서 결재란(법적 증빙) 칸 수를 왜곡하지 않는다.
  if (!hasAuthorRow && report.authorSignedAt) {
    nodes.unshift({
      key: 'author',
      label: AUTHOR_STEP_LABEL,
      name: report.authorSignerName || authorDisplayName || '-',
      at: report.authorSignedAt,
      status: 'done',
      isAuthor: true,
      isOrphan: false,
    });
  }

  // 결재 대기 중인데 current 노드가 하나도 없으면 첫 미처리 결재 단계를 현재 단계로 본다.
  // 두 경로로 이 상태가 된다:
  //  (1) currentPendingSignature가 null인 legacy 건 — 정의엔 다음 단계가 있는데 서명행이 아직 없음.
  //  (2) currentPendingSignature가 **작성자 칸에 흡수**된 건 — author 단계에 PENDING 서명행이
  //      남은 전환기 데이터. 흡수 자체는 중복 노드를 막으려 옳지만, 그 행이 현재 대기 행이면
  //      진행 위치가 통째로 사라진다(목록엔 떠 있는데 스텝퍼는 아무데도 진행 중이 아닌 상태).
  //  ⚠️ **대기(parked) 칸이 있으면 승격하지 않는다**(2026-08-27, S39). `parked`는 `current`가
  //     아니므로 이 폴백이 그냥 돌면 **막힌 칸 뒤의 단계**를 "진행 중"으로 올려, 실제로 멈춰
  //     있는 자리보다 앞서 진행되는 것처럼 보인다. 진행 위치가 없어 보이는 것이 아니라
  //     **잘못된 위치가 보이는 것**이 더 나쁘다 — 여기서 멈춘 게 맞다.
  const hasParked = nodes.some((node) => node.status === 'parked');
  if (
    report.status === 'PENDING_APPROVAL' &&
    !hasParked &&
    !nodes.some((node) => node.status === 'current')
  ) {
    const nextIndex = nodes.findIndex((node) => !node.isAuthor && node.status === 'wait');
    if (nextIndex >= 0) {
      nodes[nextIndex] = { ...nodes[nextIndex], status: 'current' };
    }
  }

  return nodes;
}

/** 스텝퍼 시각 표기 — `MM.DD HH:mm` (로컬 시간). */
export function formatFlowTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * 대기시간 기준 시각 = **이번 라운드가 제출된 시각**.
 *
 * 제출 시 그 라운드의 ApprovalSignature 행이 한 트랜잭션에서 모두 생성되므로 최신 round
 * 서명행의 최소 createdAt이 곧 제출 시각이고, 반려 후 재제출하면 새 round 행이 생겨 자동으로
 * 재제출 시점으로 리셋된다. `report.createdAt`은 초안 생성 시각이라 작성에 걸린 시간까지
 * 대기로 부풀리고 재제출에도 반응하지 않는다. `authorSignedAt`은 제출 직전 서명 시각이라
 * 서명행이 없을 때의 차선책으로만 쓴다.
 */
export function resolveSubmittedAt(item: PendingApprovalItem): string {
  const { report, signatures } = item;

  const roundCreatedAt = latestRoundSignatures(signatures)
    .map((sig) => sig.createdAt)
    .filter((value): value is string => !!value)
    // 문자열 정렬은 타임존 표기가 섞이면 어긋나므로 시각으로 비교한다.
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  return roundCreatedAt[0] ?? report.authorSignedAt ?? report.createdAt;
}

/** 대기시간 라벨 — 1시간 미만 "방금", 하루 미만 "N시간 대기", 그 외 "N일 M시간 대기". */
export function formatWaitDuration(fromIso: string, now: number = Date.now()): string {
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from)) return '-';

  const minutes = Math.floor((now - from) / 60_000);
  if (minutes < 60) return '방금';

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 대기`;

  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours > 0 ? `${days}일 ${restHours}시간 대기` : `${days}일 대기`;
}
